import { Component, Prop, h, Element } from '@stencil/core';

const COMPONENT_NAME = 'awards-type-1';

/**
 * Simple awards component displaying logos in a row with heading
 * @slot heading - {required}, <me-heading variant="2|4">
 * @category Awards
 * @example
 *   <awards-type-1 badges='[{"src": "https://upload.wikimedia.org/wikipedia/en/thumb/3/38/G2_Crowd_logo.svg/250px-G2_Crowd_logo.svg.png", "alt": "G2 Crowd Award", "link": "https://www.g2.com"}, {"src": "https://upload.wikimedia.org/wikipedia/en/thumb/3/38/G2_Crowd_logo.svg/250px-G2_Crowd_logo.svg.png", "alt": "G2 Crowd Award", "link": "https://www.g2.com/products"}, {"src": "https://upload.wikimedia.org/wikipedia/en/thumb/3/38/G2_Crowd_logo.svg/250px-G2_Crowd_logo.svg.png", "alt": "G2 Crowd Award", "link": "https://www.g2.com/reviews"}]'>
 *     <me-heading slot="heading" variant="2">Medium length section heading</me-heading>
 *   </awards-type-1>
 */
@Component({
  tag: 'awards-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class AwardsType1 {
  @Element() el!: HTMLElement;

  /**
   * @default text-left
   */
  @Prop() variant?: 'text-left' | 'text-right';

  /**
   * Use smaller text size for long headings
   * @default false
   */
  @Prop() longText: boolean = false;

  /**
   * Theme variant
   * @default light
   */
  @Prop() theme: 'light' | 'dark' = 'light';

  /**
   * List of badge logos to display
   * @type {array}
   * @items.type {object}
   * @items.property {string} src - Image source URL
   * @items.property {string} alt - Image alt text
   * @items.property {string} link - Optional URL to make logo clickable
   * @items.property {string} title - Optional title attribute for the link
   * @items.required src
   * @items.required alt
   * @minItems 1
   */
  @Prop() badges!: any[];

  render() {
    if (!this.badges || this.badges.length === 0) {
      return null;
    }

    const isTextRight = this.variant === 'text-right';

    return (
      <me-section
        class={`${COMPONENT_NAME} ${!this.variant ? `${COMPONENT_NAME}--text-left` : `${COMPONENT_NAME}--${this.variant}`} ${COMPONENT_NAME}--${this.theme} ${this.longText ? `${COMPONENT_NAME}--long-text` : ''}`}
        yGap="2xl"
        yGapLg="5xl"
        role="region"
        aria-label="Awards and Recognition"
      >
        <me-row align="center" direction="column" yGap="2xl" yGapLg="none" directionLg={isTextRight ? 'row-reverse' : 'row'} class={`${COMPONENT_NAME}__container`}>
          <me-column span="12" spanMd="6" class={`${COMPONENT_NAME}__text`}>
            <slot name="heading"></slot>
          </me-column>

          <me-column span="12" spanMd="6" class={`${COMPONENT_NAME}__logos`}>
            <div class={`${COMPONENT_NAME}__logos-list`}>
              {this.badges.map((badge, index) => {
                // Extract organization name from URL if possible
                // For example: "https://www.g2.com" -> "G2" or "G2 Crowd"
                let organizationName = badge.alt;
                if (badge.link) {
                  try {
                    const url = new URL(badge.link);
                    const hostname = url.hostname.replace('www.', '');
                    // Extract domain name (e.g., "g2.com" -> "G2")
                    const domainParts = hostname.split('.');
                    if (domainParts.length > 0) {
                      const domainName = domainParts[0];
                      // Capitalize first letter
                      organizationName = domainName.charAt(0).toUpperCase() + domainName.slice(1);
                      // Handle known organizations
                      if (domainName === 'g2') {
                        organizationName = 'G2';
                      }
                    }
                  } catch {
                    // If URL parsing fails, use alt text
                    organizationName = badge.alt;
                  }
                }

                return (
                  <div class={`${COMPONENT_NAME}__logo-item`} key={index} itemScope
                       itemType="https://schema.org/CreativeWork">
                    {badge.link ? (
                      <a href={badge.link} target="_blank" rel="noopener noreferrer" title={badge.title}
                         aria-label={badge.title}>
                        <img src={badge.src} alt={badge.alt}
                             class={`${COMPONENT_NAME}__logo-image`} {...{ fetchpriority: "high" }}
                             itemProp="image" />
                      </a>
                    ) : (
                      <img src={badge.src} alt={badge.alt}
                           class={`${COMPONENT_NAME}__logo-image`} {...{ fetchpriority: "high" }}
                           itemProp="image" />
                    )}
                    <meta itemProp="name" content={badge.alt} />
                    {badge.link && (
                      <div itemProp="author" itemScope itemType="https://schema.org/Organization">
                        <meta itemProp="name" content={organizationName} />
                        <meta itemProp="url" content={badge.link} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
